import DOMPurify from 'dompurify';

/** Shared, side-effect-free paper correction transformations. */
export const CORRECTION_TYPES = [
  { value: '1', label: 'Answer Correction' },
  { value: '2', label: 'Question Correction' },
  { value: '3', label: 'Bonus' },
];

// IDs from the existing FacultyPaperCorrection form; IDs are scoped to type.
export const REASONS_BY_TYPE = {
  '1': [
    { value: '1', label: 'Faculty Paper Not Checked Properly' },
    { value: '2', label: 'SMD Operator Mistake' },
  ],
  '2': [
    { value: '1', label: 'Language Correction' },
    { value: '2', label: 'SMD Operator Mistake' },
    { value: '3', label: 'Faculty Paper Not Checked Properly' },
  ],
  '3': [
    { value: '1', label: 'Out of Syllabus' },
    { value: '2', label: 'No Correct Option' },
    { value: '3', label: 'Data Wrong' },
    { value: '4', label: 'Figure Not Found' },
    { value: '5', label: 'Faculty Paper Not Checked Properly' },
    { value: '6', label: 'SMD Operator Mistake' },
  ],
};

export const asRecords = (value) =>
  Array.isArray(value) ? value : value && typeof value === 'object' ? Object.values(value) : [];

const normalized = (value) => String(value ?? '').trim().toLocaleLowerCase();

/** Match whole labels, never substring matches such as 'KOTA' vs 'KOTARA'. */
export const matchesAny = (field, selectedLabels) => {
  if (!selectedLabels.length) return false;
  const parts = String(field ?? '').split(/[,;|]/).map(normalized).filter(Boolean);
  return selectedLabels.some((label) => parts.includes(normalized(label)));
};

export const filterPublishedPapers = (papers, filters, masters) => {
  const session = masters.session.find((v) => String(v.session_id) === String(filters.session));
  const selectedCenters = masters.center.filter((v) => filters.center.includes(String(v.center_id))).map((v) => v.center);
  const selectedTargets = masters.target.filter((v) => filters.target.includes(String(v.target_id))).map((v) => v.target);
  const stclass = masters.stclass.find((v) => String(v.class_id) === String(filters.stclass));
  if (!session || !stclass || !selectedCenters.length || !selectedTargets.length) return [];
  return papers.filter((paper) =>
    normalized(paper.SESSION) === normalized(session.session) &&
    matchesAny(paper.center, selectedCenters) &&
    matchesAny(paper.target, selectedTargets) &&
    matchesAny(paper.class, [stclass.class])
  );
};

export const subjectsFromPaper = (paper) => {
  if (!paper) return [];
  const seen = new Set();
  return asRecords(paper.faculty)
    .filter((faculty) => faculty && faculty.sub_id != null && faculty.sub_name)
    .reduce((options, faculty) => {
      const id = String(faculty.sub_id);
      if (!seen.has(id)) {
        seen.add(id);
        options.push({ value: id, label: faculty.sub_name });
      }
      return options;
    }, []);
};

export const questionKey = (q) => String(q.matrix_id ?? `${q.paper_id}:${q.sub_id}:${q.user_id}:${q.qno}:${q.qid}`);
export const prepareQuestions = (raw, subjectId) => {
  const records = asRecords(raw)
    .filter((q) => q && String(q.sub_id) === String(subjectId))
    .map((q) => ({ ...q, _key: questionKey(q) }));
  // Faculty group alphabetical, and ORIGINAL qno within group; sequential UI numbers follow this sort.
  records.sort((a, b) =>
    String(a.faculty || '').localeCompare(String(b.faculty || ''), undefined, { sensitivity: 'base', numeric: true }) ||
    String(a.user_id ?? '').localeCompare(String(b.user_id ?? ''), undefined, { numeric: true }) ||
    Number(a.qno) - Number(b.qno) ||
    String(a.matrix_id ?? a.qid).localeCompare(String(b.matrix_id ?? b.qid), undefined, { numeric: true })
  );
  return records;
};

// Display only; never use the truncated text in API payloads.
export const visibleQuestionText = (html, max = 54) => {
  if (typeof document === 'undefined') return String(html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);
  const holder = document.createElement('div');
  holder.innerHTML = DOMPurify.sanitize(String(html ?? ''), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  const text = (holder.textContent || '').replace(/\s+/g, ' ').trim();
  return Array.from(text).length > max ? `${Array.from(text).slice(0, Math.max(0, max - 1)).join('')}…` : text || '(Question text unavailable)';
};

export const answerKind = (typeId) => {
  const type = Number(typeId);
  if ([3, 5, 6].includes(type)) return 'single';
  if (type === 7) return 'multiple';
  if ([2, 17].includes(type)) return 'integer';
  if (type === 16) return 'boolean';
  if ([1, 8, 9, 10].includes(type)) return 'matching';
  if ([11, 12, 13].includes(type)) return 'subjective';
  if (type === 15) return 'fill';
  if (type === 14) return 'caseStudy';
  return 'text'; // Unmapped paper types: do not guess an answer encoding.
};

const OPTION_RE = /^[A-F]$/;
export const validateCorrection = (row, question) => {
  if (!row?.correction_type_id || !REASONS_BY_TYPE[row.correction_type_id]?.some((r) => r.value === row.correction_reason_type_id)) {
    return 'Select a correction type and its matching reason.';
  }
  if (row.correction_type_id !== '1') return '';
  const newAnswer = String(row.new_answer ?? '').trim();
  if (!newAnswer) return 'New Answer is required for Answer Correction.';
  switch (answerKind(question.type_id)) {
    case 'single': return OPTION_RE.test(newAnswer.toUpperCase()) ? '' : 'Select one option A–F.';
    case 'multiple': return newAnswer.split(',').length === new Set(newAnswer.split(',')).size &&
      newAnswer.split(',').every((v) => OPTION_RE.test(v.trim().toUpperCase())) ? '' : 'Select distinct options A–F.';
    case 'integer': return /^[-+]?\d+$/.test(newAnswer) ? '' : 'Enter a valid integer.';
    case 'boolean': return ['True', 'False'].includes(newAnswer) ? '' : 'Select True or False.';
    case 'matching': return newAnswer.length <= 5000 ? '' : 'Matching answer is too long.';
    case 'subjective': return newAnswer.length <= 5000 ? '' : 'Subjective answer is too long.';
    case 'fill': return newAnswer.length <= 5000 ? '' : 'Fill-in answer is too long.';
    case 'caseStudy': return newAnswer.length <= 5000 ? '' : 'Case-study answer is too long.';
    default: return newAnswer.length <= 5000 ? '' : 'Answer must be 5,000 characters or fewer.';
  }
};

/** Compatibility: legacy header-level created_by plus new per-question created_by. */
export const buildCorrectionPayload = (paperId, subjectId, questions, drafts) => {
  const selected = questions.filter((q) => drafts[q._key]);
  return {
    paper_id: paperId,
    sub_id: subjectId,
    total_correction_no: selected.length,
    correction_data: selected.map((q) => ({
      q_no: q.qno, // Do NOT substitute the continuous display index.
      matrix_id: q.matrix_id,
      qid: q.qid,
      created_by: q.user_id,
      correction_type_id: drafts[q._key].correction_type_id,
      correction_reason_type_id: drafts[q._key].correction_reason_type_id,
      old_correction: drafts[q._key].correction_type_id === '1' ? String(q.answer ?? '') : '',
      new_correction: drafts[q._key].correction_type_id === '1' ? drafts[q._key].new_answer.trim() : '',
    })),
  };
};

/** Distinct paper-matrix corrections, not duplicate submission/history rows. */
export const uniqueCorrectionHistory = (history) => {
  const results = new Map();
  history.forEach((row, index) => {
    // Original qno is not a safe unique key: use matrix or correction detail identity.
    const key = row.matrix_id != null
      ? `${row.paper_id}:${row.matrix_id}`
      : row.correction_detail_id != null ? `detail:${row.correction_detail_id}` : `unidentified:${index}`;
    const previous = results.get(key);
    const timestamp = (item) => {
      const value = item.updated_at || item.updated_on || item.created_at || item.created_on;
      const date = value ? Date.parse(String(value)) : NaN;
      return Number.isNaN(date) ? null : date;
    };
    // Prefer the newest correction when its timestamps are known. Otherwise
    // use the last row; backend should enforce one active record per matrix.
    if (!previous || timestamp(row) == null || timestamp(previous) == null || timestamp(row) >= timestamp(previous)) {
      results.set(key, row);
    }
  });
  return [...results.values()];
};
